import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  ICD10Code,
  useGetICD10CodeReplacementsQuery
} from "services/icd10-codes";

import { inactiveIcd10ReplacementsTestId } from "./icd10-inactive.constants";

// Enough for every ear/side/laterality split in the hearing chapter (H93.2 has 9),
// few enough to scan; past it the user is told to narrow the search instead.
export const MAX_REPLACEMENTS_SHOWN = 24;

interface InactiveICD10ReplacementsProps {
  code: ICD10Code;
  onReplace: (replacement: ICD10Code) => void;
  onRemove: () => void;
}

/**
 * Shown under the ICD-10 picker for every selected code that is inactive.
 *
 * Most inactive codes are headings from the ICD-10-CM outline (H61.2 "Impacted
 * cerumen"), which payers reject; the billable codes sit one or two levels
 * beneath them (H61.21 right ear, H61.22 left ear, H61.23 both). The panel
 * names them as buttons, and one click swaps the heading for the pick in place.
 * A code CMS retired outright has no codes beneath it, so the panel says to
 * search for the current one instead.
 */
export default function InactiveICD10Replacements({
  code,
  onReplace,
  onRemove
}: InactiveICD10ReplacementsProps) {
  const {
    data: replacements,
    isLoading,
    isError
  } = useGetICD10CodeReplacementsQuery(code.id);

  const hasReplacements = Boolean(replacements && replacements.length > 0);
  // A hint, not a catalogue: a broad category (E11, S52) has a hundred leaves.
  const shown = replacements?.slice(0, MAX_REPLACEMENTS_SHOWN) ?? [];
  const hiddenCount = (replacements?.length ?? 0) - shown.length;

  return (
    <Alert
      severity="warning"
      sx={{ mt: 1 }}
      data-testid={inactiveIcd10ReplacementsTestId(code.code)}
    >
      {/* In this theme body2 is 16px and body1 14px, and AlertTitle takes body1;
          the title is the fact a user looks for first, so it gets body2 and a heavier weight. */}
      <AlertTitle
        sx={{ typography: "body2", fontWeight: "fontWeightSemiBold" }}
      >
        {code.code} &ldquo;{code.description}&rdquo; is not a billable code
      </AlertTitle>
      {isLoading ? (
        <Typography variant="body2">
          Looking up the codes to use instead…
        </Typography>
      ) : hasReplacements ? (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            It is a heading in the ICD-10 list, and insurance rejects it. Pick
            the specific code below and it will replace {code.code} in the list
            above.
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
            {shown.map((replacement) => (
              <Button
                key={replacement.id}
                variant="outlined"
                color="inherit"
                onClick={() => onReplace(replacement)}
                data-testid={`icd10_replacement_${replacement.code}`}
                sx={{ textTransform: "none", textAlign: "left" }}
              >
                {replacement.code}: {replacement.description}
              </Button>
            ))}
          </Stack>
          {hiddenCount > 0 && (
            <Typography
              variant="body2"
              sx={{ mt: 1 }}
              data-testid={`icd10_replacements_more_${code.code}`}
            >
              Showing the first {shown.length} of {replacements?.length}. Type
              more of the code, or the side or ear, in the search above to find
              the rest.
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2">
          {isError
            ? "The codes to use instead could not be loaded. "
            : "This code is no longer in use and has no direct replacement. "}
          Search for the current code above, then remove this one.
        </Typography>
      )}
      <Button
        color="inherit"
        onClick={onRemove}
        data-testid={`icd10_remove_inactive_${code.code}`}
        sx={{ mt: 1, textTransform: "none" }}
      >
        Remove {code.code}
      </Button>
    </Alert>
  );
}
